import type { UsageRequestLogEntry } from '@nadoutong/contracts';
import { Fragment, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { PlatformApiError } from '../api/client.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useUsageRequests } from '../api/useUsage.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { formatTimestamp, formatTokens, formatUsdCents } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { INPUT_CLASS, INPUT_CLASS_MONO } from '../styles/forms.ts';

const PAGE_SIZE = 50;

type RangeOption = {
  readonly id: string;
  readonly labelKey: I18nKey;
  readonly days: number;
};

const RANGE_OPTIONS: readonly RangeOption[] = [
  { id: '24h', labelKey: 'requests.range.24h', days: 1 },
  { id: '7d', labelKey: 'requests.range.7d', days: 7 },
  { id: '30d', labelKey: 'requests.range.30d', days: 30 },
];

function rangeFromDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function Requests(): JSX.Element {
  const t = useT();
  const keysQ = useKeysList();
  const [selected, setSelected] = useState<RangeOption>(
    RANGE_OPTIONS[1] ?? RANGE_OPTIONS[0] ?? { id: '7d', labelKey: 'requests.range.7d', days: 7 },
  );
  const [requestIdInput, setRequestIdInput] = useState('');
  const [requestId, setRequestId] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const range = useMemo(() => rangeFromDays(selected.days), [selected]);
  const requestsQ = useUsageRequests({
    ...range,
    requestId,
    apiKeyId,
    status: status === 'success' || status === 'error' ? status : undefined,
    limit: PAGE_SIZE,
    offset,
  });
  const rows = requestsQ.data?.rows ?? [];
  const total = requestsQ.data?.total ?? 0;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  function resetPaging(): void {
    setOffset(0);
    setExpandedId(null);
  }

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setRequestId(requestIdInput.trim());
    resetPaging();
  }

  return (
    <section data-testid="requests-page" className="flex flex-col gap-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
            {t('requests.title')}
          </h2>
          <p className="text-sm text-outline">{t('requests.subtitle')}</p>
        </div>
        <div
          role="radiogroup"
          aria-label={t('requests.range.aria_label')}
          data-testid="requests-range-picker"
          className="inline-flex items-center gap-1 rounded-lg bg-surface-container-low p-1"
        >
          {RANGE_OPTIONS.map((opt) => {
            const isActive = opt.id === selected.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => {
                  setSelected(opt);
                  resetPaging();
                }}
                data-testid={`requests-range-${opt.id}`}
                className={[
                  'inline-flex items-center justify-center rounded-lg px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors',
                  isActive
                    ? 'bg-primary-container text-on-primary'
                    : 'bg-transparent text-outline hover:text-on-surface',
                ].join(' ')}
              >
                {t(opt.labelKey)}
              </button>
            );
          })}
        </div>
      </header>

      <article className="flex flex-col gap-4 rounded-xl bg-surface-container p-6">
        <form
          className="grid gap-4 lg:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(150px,0.7fr)_auto]"
          onSubmit={handleSearch}
        >
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('requests.filter.request_id')}
            </span>
            <input
              value={requestIdInput}
              onChange={(e) => setRequestIdInput(e.currentTarget.value)}
              placeholder={t('requests.filter.request_id_placeholder')}
              data-testid="requests-filter-request-id"
              className={INPUT_CLASS_MONO}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('requests.filter.key')}
            </span>
            <select
              value={apiKeyId}
              onChange={(e) => {
                setApiKeyId(e.currentTarget.value);
                resetPaging();
              }}
              data-testid="requests-filter-key"
              className={INPUT_CLASS}
            >
              <option value="">{t('requests.filter.key_all')}</option>
              {(keysQ.data?.keys ?? []).map((key) => (
                <option key={key.id} value={key.id}>
                  {key.name ?? t('requests.key.unnamed')}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('requests.filter.status')}
            </span>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.currentTarget.value);
                resetPaging();
              }}
              data-testid="requests-filter-status"
              className={INPUT_CLASS}
            >
              <option value="">{t('requests.filter.status_all')}</option>
              <option value="success">{t('requests.filter.status_success')}</option>
              <option value="error">{t('requests.filter.status_error')}</option>
            </select>
          </label>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              data-testid="requests-filter-submit"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-primary-container px-4 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-primary-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
                search
              </span>
              <span>{t('requests.filter.submit')}</span>
            </button>
            {requestId.length > 0 ? (
              <button
                type="button"
                data-testid="requests-filter-clear"
                onClick={() => {
                  setRequestId('');
                  setRequestIdInput('');
                  resetPaging();
                }}
                className="inline-flex h-10 items-center justify-center rounded-lg bg-surface-container-highest px-3 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
              >
                {t('requests.filter.clear')}
              </button>
            ) : null}
          </div>
        </form>

        {requestsQ.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="requests-loading">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
            <span className="sr-only">{t('requests.loading')}</span>
          </div>
        ) : requestsQ.isError ? (
          <p className="text-sm text-error" role="alert" data-testid="requests-error">
            {requestsQ.error instanceof PlatformApiError
              ? `${requestsQ.error.code}: ${requestsQ.error.message}`
              : t('requests.error')}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-outline" data-testid="requests-empty">
            {t('requests.empty')}
          </p>
        ) : (
          <>
            <RequestTable
              rows={rows}
              expandedId={expandedId}
              onToggle={(id) => setExpandedId((current) => (current === id ? null : id))}
            />
            <footer className="flex flex-wrap items-center justify-between gap-3 text-xs text-outline">
              <span data-testid="requests-page-count">
                {t('requests.pagination.summary', {
                  from: offset + 1,
                  to: Math.min(offset + PAGE_SIZE, total),
                  total,
                })}
              </span>
              <div className="inline-flex items-center gap-2">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={() => {
                    setOffset(Math.max(0, offset - PAGE_SIZE));
                    setExpandedId(null);
                  }}
                  data-testid="requests-page-prev"
                  className="rounded-lg bg-surface-container-highest px-3 py-2 font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('requests.pagination.prev')}
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => {
                    setOffset(offset + PAGE_SIZE);
                    setExpandedId(null);
                  }}
                  data-testid="requests-page-next"
                  className="rounded-lg bg-surface-container-highest px-3 py-2 font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('requests.pagination.next')}
                </button>
              </div>
            </footer>
          </>
        )}
      </article>
    </section>
  );
}

function RequestTable({
  rows,
  expandedId,
  onToggle,
}: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly expandedId: string | null;
  readonly onToggle: (id: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1100px] border-collapse text-sm" data-testid="requests-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
            <th className="px-3 py-2 font-semibold">{t('requests.column.time')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.request')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.key')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.model')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.status')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.duration')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.input')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.output')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.cache_read')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.cache_write')}</th>
            <th className="px-3 py-2 font-semibold">{t('requests.column.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isExpanded = expandedId === row.id;
            return (
              <Fragment key={row.id}>
                <tr
                  className="cursor-pointer odd:bg-surface-container-low hover:bg-surface-container-high"
                  data-testid={`requests-row-${row.id}`}
                  onClick={() => onToggle(row.id)}
                >
                  <td className="px-3 py-2 font-mono text-outline">
                    {formatTimestamp(row.createdAt)}
                  </td>
                  <td className="px-3 py-2 font-mono text-on-surface">
                    {shortRequestId(row.requestId)}
                  </td>
                  <td className="px-3 py-2 font-semibold text-on-surface">
                    {keyDisplayName(row.apiKeyName, row.apiKeyPrefix, t)}
                  </td>
                  <td className="px-3 py-2 font-mono text-on-surface-variant">
                    <span className="flex flex-col gap-1">
                      <span>{row.model}</span>
                      <span className="text-[11px] text-outline">{row.vendor}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <StatusChip
                      variant={row.statusCode >= 400 ? 'error' : 'ok'}
                      label={String(row.statusCode)}
                      testId={`requests-status-${row.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-on-surface-variant">
                    {row.latencyMs === null
                      ? '—'
                      : t('requests.duration.ms', { value: row.latencyMs })}
                  </td>
                  <td className="px-3 py-2 font-mono text-on-surface-variant">
                    {formatTokens(row.inputTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-on-surface-variant">
                    {formatTokens(row.outputTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-on-surface-variant">
                    {formatTokens(row.cacheReadTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-on-surface-variant">
                    {formatTokens(row.cacheWriteTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-primary">
                    {formatUsdCents(row.costUsdCents)}
                  </td>
                </tr>
                {isExpanded ? (
                  <tr key={`${row.id}-details`} data-testid={`requests-row-details-${row.id}`}>
                    <td colSpan={11} className="bg-surface-container-lowest px-3 py-3">
                      <dl className="grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
                        <Detail label={t('requests.detail.request_id')} value={row.requestId} />
                        <Detail label={t('requests.detail.api_key_id')} value={row.apiKeyId} />
                        <Detail label={t('requests.detail.protocol')} value={row.protocol} />
                      </dl>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Detail({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">{label}</dt>
      <dd className="break-all font-mono text-on-surface-variant">{value}</dd>
    </div>
  );
}

function shortRequestId(value: string): string {
  if (value.length <= 18) return value;
  return `${value.slice(0, 10)}…${value.slice(-6)}`;
}

function keyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof prefix === 'string' && prefix.length > 0) return t('requests.key.unnamed');
  return t('requests.key.unknown');
}
